import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useMemo, useRef } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	ArrowLeftOutline,
	Button,
	Display,
	Flex,
	HStack,
	Stack,
	useDisclosure,
	useToast,
} from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';

import { AdminPageContainer } from '../../../components/layout/admin-page-container';
import { PageTitle } from '../../../components/page/page-title';
import { extractBackendErrorMessage } from '../../../queries/meta';
import { useDeletePointExpiryPeriod, usePointExpiryPeriod, useUpdatePointExpiryPeriod } from '../../../queries/point-expiry';
import { POINT_EXPIRY_INDEX_PATH, toFormValues, toPayload } from '../point-expiry-form-helpers';
import { createPointExpiryFormSchema, PointExpiryFormData } from '../schema';
import { PointExpiryForm } from './point-expiry-form';

export const EditPointExpiryPage: FC = () => {
	const { t } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods.form' });
	const toast = useToast();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	assertPresence(id);

	// Data is prefetched by the route loader, so the record is present on mount.
	const { data: period } = usePointExpiryPeriod(id);
	assertPresence(period);

	const formSchema = useMemo(() => createPointExpiryFormSchema(t), [t]);

	const form = useForm<PointExpiryFormData>({
		resolver: zodResolver(formSchema) as unknown as Resolver<PointExpiryFormData>,
		defaultValues: toFormValues(period),
	});

	// Re-seed the form when navigating between edit URLs.
	useEffect(() => {
		form.reset(toFormValues(period));
	}, [form, id]);

	const { mutateAsync: updatePeriod, isLoading } = useUpdatePointExpiryPeriod(period.id);

	const deleteConfirm = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const { mutate: deletePeriod, isLoading: isDeleting } = useDeletePointExpiryPeriod({
		onSuccess: () => {
			deleteConfirm.onClose();
			toast({ title: t('deleteSuccess'), status: 'success' });
			navigate(POINT_EXPIRY_INDEX_PATH);
		},
		onError: (error) => {
			toast({ title: extractBackendErrorMessage(error) ?? t('saveFailure'), status: 'error' });
		},
	});

	const handleSubmit = async (data: PointExpiryFormData) => {
		try {
			await updatePeriod(toPayload(data));
			toast({ title: t('updateSuccess'), status: 'success' });
			navigate(POINT_EXPIRY_INDEX_PATH);
		} catch (error) {
			toast({ title: extractBackendErrorMessage(error) ?? t('saveFailure'), status: 'error' });
		}
	};

	return (
		<>
			<PageTitle>{t('editHeader')}</PageTitle>

			<AdminPageContainer width="narrow">
				<Stack spacing={6}>
					<HStack>
						<Button as={Link} variant="link" gap={1} to={POINT_EXPIRY_INDEX_PATH} type="button">
							<ArrowLeftOutline size="xs" />
							{t('back')}
						</Button>
					</HStack>

					<Display size="sm">{t('editHeader')}</Display>

					<PointExpiryForm form={form} onSubmit={handleSubmit}>
						<HStack justify="space-between" width="100%">
							<Button variant="ghost" colorScheme="danger" type="button" onClick={deleteConfirm.onOpen} data-testid="delete-period-button">
								{t('cancelCta')}
							</Button>
							<Button type="submit" isLoading={isLoading} isDisabled={!form.formState.isDirty}>
								{t('saveCta')}
							</Button>
						</HStack>
					</PointExpiryForm>
				</Stack>
			</AdminPageContainer>

			<AlertDialog leastDestructiveRef={cancelRef} isOpen={deleteConfirm.isOpen} onClose={deleteConfirm.onClose} size="lg">
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold" borderBottom="none">
							{t('deleteHeader')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>{t('deleteBody')}</AlertDialogBody>
						<AlertDialogFooter>
							<Flex justify="space-between" w="full">
								<Button ref={cancelRef} onClick={deleteConfirm.onClose} variant="alpha" colorScheme="secondary">
									{t('deleteDialogCancel')}
								</Button>
								<Button colorScheme="danger" variant="solid" isLoading={isDeleting} onClick={() => deletePeriod(period.id)} ml={3}>
									{t('deleteDialogConfirm')}
								</Button>
							</Flex>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};
